import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { Store, Truck, Users, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { formatRands, statusLabel } from "@/lib/order";
import { StaffShell } from "@/components/layout/StaffShell";
import { adminNav } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { CenterSpinner } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusViews";
import { shortId } from "@/lib/order";

const PIE = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function AdminOverview() {
  const analytics = useQuery({ queryKey: ["admin-analytics"], queryFn: () => api("/admin/analytics") });
  const orders = useQuery({ queryKey: ["admin-orders"], queryFn: () => api("/admin/orders") });

  if (analytics.isLoading || orders.isLoading) {
    return <StaffShell items={adminNav} roleLabel="Admin" title="Overview"><CenterSpinner /></StaffShell>;
  }

  const a = analytics.data || {};
  const all = orders.data || [];

  // revenue by day (last 7 buckets)
  const byDay = {};
  all.filter((o) => !["CANCELLED", "REFUNDED"].includes(o.status)).forEach((o) => {
    const d = (o.created_at || "").slice(0, 10);
    byDay[d] = (byDay[d] || 0) + (o.total || 0);
  });
  const series = Object.entries(byDay).sort().slice(-7).map(([day, amount]) => ({ day: day.slice(5), amount: Math.round(amount) }));

  const statusCounts = {};
  all.forEach((o) => (statusCounts[o.status] = (statusCounts[o.status] || 0) + 1));
  const pie = Object.entries(statusCounts).map(([name, value]) => ({ name: statusLabel(name), value }));

  return (
    <StaffShell items={adminNav} roleLabel="Admin" title="Overview">
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Kpi icon={Wallet} label="Revenue" value={formatRands(a.revenue)} />
          <Kpi icon={Store} label="Approved vendors" value={a.vendors ?? 0} sub={`${a.pending_vendors ?? 0} pending`} />
          <Kpi icon={Users} label="Customers" value={a.customers ?? 0} />
          <Kpi icon={Truck} label="Drivers online" value={a.drivers_online ?? 0} />
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="rounded-2xl p-5 lg:col-span-2">
            <h2 className="mb-4 font-display text-lg">Revenue (recent days)</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series}>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tickLine={false} axisLine={false} fontSize={12} />
                  <Tooltip formatter={(v) => formatRands(v)} cursor={{ fill: "hsl(var(--muted))" }} />
                  <Bar dataKey="amount" radius={[8, 8, 0, 0]} fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="rounded-2xl p-5">
            <h2 className="mb-4 font-display text-lg">Orders by status</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {pie.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <Card className="rounded-2xl p-5">
          <h2 className="mb-4 font-display text-lg">Recent orders</h2>
          <div className="space-y-2">
            {all.slice(0, 8).map((o) => (
              <div key={o.order_id} className="flex items-center gap-3 border-b py-2 last:border-0" data-testid={`admin-order-${o.order_id}`}>
                <span className="w-20 text-sm text-muted-foreground">{shortId(o.order_id)}</span>
                <span className="flex-1 truncate text-sm">{o.vendor_name} → {o.customer_name}</span>
                <span className="text-sm">{formatRands(o.total)}</span>
                <StatusBadge status={o.status} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </StaffShell>
  );
}

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <Card className="rounded-2xl p-5">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="font-display text-xl">{value}</p>
        </div>
      </div>
      {sub && <p className="mt-2 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}
